import { Directive, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { marker as _ } from '@colsen1991/ngx-translate-extract-marker';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { AuthenticationFacade, FailedLoginInfo, LoginError, LoginWithPasswordError } from '../authentication';
import { BaseError, UserActivationMissingError } from '../error-classifier';
import { ConfigSettingsService, LogService, MessageBase } from '../providers';

@Directive()
export abstract class AuthHandler implements OnInit, OnDestroy {
  private readonly config = inject(ConfigSettingsService);
  private readonly messageService = inject(MessageBase);
  private readonly translateService = inject(TranslateService);

  public isExternalLoginEnabled = signal(false);
  public failedLoginInfo = signal<FailedLoginInfo | undefined>(undefined);

  protected readonly auth = inject(AuthenticationFacade);
  protected readonly route = inject(ActivatedRoute, { optional: true });
  protected destroyed$ = new Subject<boolean>();

  public classifyError(error: LoginWithPasswordError): BaseError {
    if (error.error && error.error === 'server.error.auth.user_activation_missing') {
      return new UserActivationMissingError(this.messageService);
    }

    return new BaseError(this.messageService, error.error);
  }

  public async loginWithCodeFlow(): Promise<void> {
    await this.auth.authenticate();
    this.onCodeLogin();
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  public ngOnInit(): void {
    this.isExternalLoginEnabled.set(this.config.appSettings.supportedOAuthGrants.has('code'));

    if (!this.isExternalLoginEnabled()) {
      return;
    }

    this.route?.queryParams
      .pipe(
        takeUntil(this.destroyed$),
        filter((params) => 'error' in params),
      )
      .subscribe(async ({ error, error_description }) => {
        if (error) {
          const loginError = new LoginError(error_description ?? 'general.text.error', error);
          LogService.error(`${loginError.name}: ${loginError.message}`);
          await this.handleCodeLoginError(loginError);
        }
      });
  }

  /**
   * Callback that is called if a login by oauth code flow fails
   */
  protected abstract handleCodeLoginError(error: LoginError): Promise<void>;

  /**
   * Callback that is called if a login with password attempt fails
   */
  protected abstract handleLoginWithPasswordError(
    error: LoginWithPasswordError,
    formModel: { username: string; password: string },
  ): void;

  /**
   * Callback that is called once an oauth code login succeeds
   */
  protected abstract onCodeLogin(): void;

  /**
   * Callback that is called once any loading logic should be disabled
   * @param handler the handler obtained from the {@link AuthHandler#onLoadingStart} call
   */
  protected abstract onLoadingEnd(): Promise<void>;

  /**
   * Callback that is called once some kind of loading logic should be started
   * @returns a handler that is passed to the {@link AuthHandler#onLoadingEnd} callback later
   */
  protected abstract onLoadingStart(): Promise<void>;

  protected async loginWithPassword(username: string, password: string): Promise<boolean> {
    this.failedLoginInfo?.set(undefined);
    await this.onLoadingStart();

    username = username.trim();

    try {
      await this.auth.authenticate(username, password);
      return true;
    } catch (e: unknown) {
      const loginError = this.toLoginWithPasswordError(e);

      this.handleLoginWithPasswordError(loginError, { username, password });

      const lockedUntilDate = loginError.loginLockedUntil ? new Date(loginError.loginLockedUntil) : undefined;
      const formattedLockedUntil = lockedUntilDate
        ? lockedUntilDate.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
        : undefined;

      this.failedLoginInfo.set({
        error: this.buildLoginErrorMessage(
          loginError.error,
          formattedLockedUntil,
          loginError.triesLeft,
          loginError.mailSent,
        ),
        triesLeft: loginError.triesLeft,
        loginLockedUntil: lockedUntilDate,
        mailSent: loginError.mailSent,
      });

      return false;
    } finally {
      await this.onLoadingEnd();
    }
  }

  private buildLoginErrorMessage(
    error: string,
    formattedLockedUntil: string | undefined,
    triesLeft: number | undefined,
    mailSent?: boolean,
  ): string {
    if (formattedLockedUntil && error.endsWith('loginDenied')) {
      const translatedErrorKey = mailSent
        ? _('server.error.auth.loginDeniedMailSent')
        : _('server.error.auth.loginDenied');
      return this.translateService.instant(translatedErrorKey, {
        date: formattedLockedUntil,
      });
    } else if (error.endsWith('accessDenied')) {
      const accessDenied = this.translateService.instant(_('server.error.auth.accessDenied'));

      let triesLeftInfo = '';
      if (triesLeft !== undefined) {
        const isSingular = triesLeft === 1;
        const triesLeftKey = isSingular
          ? _('server.error.auth.triesLeftInfoSingular')
          : _('server.error.auth.triesLeftInfoPlural');
        triesLeftInfo = this.translateService.instant(triesLeftKey, { count: triesLeft });
      }
      return triesLeftInfo ? `${accessDenied} ${triesLeftInfo}` : accessDenied;
    }
    return this.translateService.instant(error);
  }

  private toLoginWithPasswordError(e: unknown): LoginWithPasswordError {
    if (typeof e === 'object' && e !== null && 'error' in e) {
      const obj = e as {
        error?: string;
        error_description?: string;
        triesLeft?: number;
        loginLockedUntil?: string;
        mailSent?: boolean;
      };

      return new LoginWithPasswordError(
        obj.error ?? obj.error_description ?? 'general.text.error',
        obj.triesLeft,
        obj.loginLockedUntil,
        obj.error_description,
        obj.mailSent,
      );
    }
    return new LoginWithPasswordError('general.text.error');
  }
}
